import type { SupabaseClient } from '@supabase/supabase-js';
import type { AppState, Member, Notification } from './types';
import { rebaseValue } from './rebase';

export const NOTIFICATION_COLUMNS =
  'id,user_id,actor_id,kind,target_type,target_id,created_at,read_at';
type Row = Record<string, any>;
const names: Record<string, string> = {
  follow: 'New follower',
  follow_request: 'Follow request',
  reaction: 'A dumbbell for your post',
  comment: 'New comment',
  message: 'New message',
  forum: 'Forum activity',
  trade: 'Trade activity',
  moderation: 'Moderation update',
  system: 'Account update',
};
function path(type: string, id: string | null) {
  if (type === 'media') return '/admin?tab=photos';
  if (type === 'equipment_request') return '/admin?tab=equipment';
  if (type === 'catalog_product' && id) return `/catalog/${id}`;
  if (!id) return '/notifications';
  const routes: Record<string, string> = {
    profile: '/profile/',
    post: '/home',
    comment: '/home',
    story: '/stories',
    message: '/messages',
    conversation: '/messages/',
    forum_topic: '/forum/',
    forum_reply: '/forum',
    trade: '/trade/',
    review: '/catalog/',
  };
  const route = routes[type] ?? '/notifications';
  return route.endsWith('/') ? route + id : route;
}

export function mapNotifications(
  rows: Row[],
  userId: string,
  members: ReadonlyArray<Pick<Member, 'id' | 'display_name'>>,
  messages: Row[] = [],
): Notification[] {
  const actors = new Map(members.map((member) => [member.id, member.display_name]));
  const kinds: Record<string, string> = {
    follow: 'follows',
    follow_request: 'follows',
    reaction: 'reactions',
    comment: 'comments',
    message: 'messages',
  };
  return rows
    .filter(
      (row) =>
        row.user_id === userId && !(row.kind === 'moderation' && row.target_type === 'media'),
    )
    .map((row) => ({
      id: row.id,
      type: kinds[row.kind] ?? row.kind,
      title:
        row.kind === 'system' && row.target_type === 'message'
          ? 'Photo removed'
          : row.kind === 'moderation' && row.target_type === 'equipment_request'
            ? 'Equipment suggestion'
            : row.kind === 'system' && row.target_type === 'catalog_product'
              ? 'Equipment added to the library'
              : (names[row.kind] ?? 'Community update'),
      body:
        row.target_type === 'message' && messages.some((message) => message.id === row.target_id)
          ? String(messages.find((message) => message.id === row.target_id)!.body).slice(0, 140)
          : row.kind === 'system' && row.target_type === 'message'
            ? 'Open Messages to see the reason.'
            : row.kind === 'moderation' && row.target_type === 'equipment_request'
              ? `${actors.get(row.actor_id) ?? 'A member'} suggested equipment that is missing from the library.`
              : row.kind === 'system' && row.target_type === 'catalog_product'
                ? 'Open it and add it to your gym.'
                : (actors.get(row.actor_id) ?? 'Your garage community'),
      created_at: row.created_at,
      read: Boolean(row.read_at),
      path:
        row.target_type === 'message' && messages.some((message) => message.id === row.target_id)
          ? '/messages/' + messages.find((message) => message.id === row.target_id)!.conversation_id
          : path(row.target_type, row.target_id),
    }));
}

/** Read just the current inbox and actor names; no catalog/account reload or cross-viewer cache. */
export async function loadLiveNotifications(
  client: SupabaseClient,
  userId: string,
  members: ReadonlyArray<Pick<Member, 'id' | 'display_name'>>,
  signal: AbortSignal,
): Promise<Notification[]> {
  signal.throwIfAborted();
  const result = await client
    .from('notifications')
    .select(NOTIFICATION_COLUMNS)
    .eq('user_id', userId)
    .order('created_at', { ascending: false })
    .limit(500)
    .abortSignal(signal);
  if (result.error) throw new Error(result.error.message);
  signal.throwIfAborted();
  const rows = (result.data ?? []).filter((row) => row.user_id === userId);
  const known = new Set(members.map((member) => member.id));
  const missing = [
    ...new Set(rows.map((row) => row.actor_id).filter((id) => id && !known.has(id))),
  ];
  const extra: Array<Pick<Member, 'id' | 'display_name'>> = [];
  await Promise.all(
    Array.from({ length: Math.ceil(missing.length / 100) }, async (_, index) => {
      const ids = missing.slice(index * 100, index * 100 + 100);
      const actors = await client
        .from('profiles')
        .select('id,display_name')
        .in('id', ids)
        .limit(ids.length)
        .abortSignal(signal);
      if (actors.error) throw new Error(actors.error.message);
      for (const actor of actors.data ?? []) if (ids.includes(actor.id)) extra.push(actor);
    }),
  );
  signal.throwIfAborted();
  const messageIDs = [
    ...new Set(
      rows
        .filter((row) => row.target_type === 'message')
        .map((row) => row.target_id)
        .filter(Boolean),
    ),
  ];
  const messages: Row[] = [];
  for (let start = 0; start < messageIDs.length; start += 100) {
    const result = await client
      .from('messages')
      .select('id,conversation_id,body')
      .in('id', messageIDs.slice(start, start + 100))
      .abortSignal(signal);
    if (result.error) throw new Error(result.error.message);
    messages.push(...(result.data ?? []));
  }
  signal.throwIfAborted();
  return mapNotifications(rows, userId, [...members, ...extra], messages);
}

/** Coalesce event bursts; a later event during a read schedules one follow-up instead of losing it. */
export function createNotificationRefresh(
  client: SupabaseClient,
  userId: string,
  access: {
    getViewerId: () => string | undefined;
    getState: () => AppState;
    commit: (state: AppState) => void;
  },
) {
  let disposed = false,
    queued = false;
  let scheduled: ReturnType<typeof setTimeout> | undefined;
  let active: { controller: AbortController; timer: ReturnType<typeof setTimeout> } | undefined;
  const current = () =>
    !disposed && access.getViewerId() === userId && access.getState().current_user_id === userId;
  async function run() {
    if (!current()) return;
    queued = false;
    const before = access.getState(),
      controller = new AbortController();
    const request = {
      controller,
      timer: setTimeout(() => {
        if (active !== request) return;
        active = undefined;
        controller.abort();
        if (queued && current()) schedule();
      }, 10_000),
    };
    active = request;
    try {
      const notifications = await loadLiveNotifications(
        client,
        userId,
        before.members,
        controller.signal,
      );
      if (!current() || controller.signal.aborted || active !== request) return;
      const latest = access.getState();
      access.commit({
        ...latest,
        notifications: rebaseValue(
          before.notifications,
          latest.notifications,
          notifications,
        ) as Notification[],
      });
    } catch {
      /* Background inbox recovery must not replace a page or interrupt a draft. */
    } finally {
      // A failed actor lookup must not leave its sibling lookups running.
      controller.abort();
      clearTimeout(request.timer);
      if (active === request) {
        active = undefined;
        if (queued && current()) schedule();
      }
    }
  }
  function schedule() {
    if (!current()) return;
    queued = true;
    if (active || scheduled !== undefined) return;
    scheduled = setTimeout(() => {
      scheduled = undefined;
      void run();
    }, 100);
  }
  return {
    schedule,
    dispose() {
      disposed = true;
      queued = false;
      clearTimeout(scheduled);
      clearTimeout(active?.timer);
      active?.controller.abort();
      active = undefined;
    },
  };
}
